import {memo, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {TasksService} from '../../../../services/openapi/services/TasksService';
import {ArbeitszeitService} from '../../../../services/openapi/services/ArbeitszeitService';
import type {Task} from '../../../../services/openapi/models/Task';
import type {BauvorhabenWithID} from '../../../../services/openapi/models/BauvorhabenWithID';
import {TaskStatus} from '../../../../services/openapi/models/TaskStatus';
import TaskDetail from './task-detail';
import {performanceMonitor} from './utils/taskListOptimizations';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Collapse,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Assignment as TaskIcon,
    AttachFile as AttachFileIcon,
    Business as BusinessIcon,
    CalendarToday as CalendarIcon,
    FilterList as FilterIcon,
    KeyboardArrowRight as ArrowRightIcon,
    Person as PersonIcon,
    Restore as RestoreIcon,
    Search as SearchIcon,
} from '@mui/icons-material';
import UserContext from '../../../../Contexts/UserContext/UserContext';
import {useSessionContext} from 'supertokens-auth-react/recipe/session';

interface TaskListProps {
    onTaskCreated: () => void;
    showOnlyAssigned?: boolean;
    showArchived?: boolean;
    autoOpenTaskId?: number | null;
    onAutoOpenComplete?: () => void;
    filterByStatus?: TaskStatus;
    hideFilters?: boolean;
    preFilteredTasks?: Task[];
}

interface TaskWithAttachments extends Task {
    image_count?: number;
    document_count?: number;
    total_attachments?: number;
}

type SortField =
    | 'title'
    | 'status'
    | 'assigned_to_name'
    | 'created_by_name'
    | 'created_at'
    | 'due_date'
    | 'total_attachments';
type SortDirection = 'asc' | 'desc';

interface FilterState {
    status: TaskStatus | 'ALL';
    assignedTo: string;
    priority: 'ALL' | 'OVERDUE' | 'DUE_SOON' | 'NO_DUE_DATE';
    hasAttachments: 'ALL' | 'WITH_ATTACHMENTS' | 'WITHOUT_ATTACHMENTS';
    bauvorhaben_id: number | 'ALL';
}

export default function TaskList({onTaskCreated, showOnlyAssigned, showArchived = false, autoOpenTaskId, onAutoOpenComplete, filterByStatus, hideFilters = false, preFilteredTasks}: TaskListProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [tasks, setTasks] = useState<TaskWithAttachments[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
    const [sortField, setSortField] = useState<SortField>('due_date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showSortOptions, setShowSortOptions] = useState(false);
    const [bauvorhaben, setBauvorhaben] = useState<BauvorhabenWithID[]>([]);
    const [filters, setFilters] = useState<FilterState>({
        status: TaskStatus.OPEN,
        assignedTo: 'ALL',
        priority: 'ALL',
        hasAttachments: 'ALL',
        bauvorhaben_id: 'ALL',
    });

    const {email, name, additionalUserdata} = useContext(UserContext);
    const sessionContext = useSessionContext();
    const currentUserId = (sessionContext as any).userId;

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: isMobile ? '2-digit' : 'numeric'
        });
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isOverdue = (dueDate: string) => {
        return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
    };

    const isDueSoon = (dueDate: string) => {
        const due = new Date(dueDate);
        const today = new Date();
        const threeDaysFromNow = new Date();
        threeDaysFromNow.setDate(today.getDate() + 3);
        return due >= today && due <= threeDaysFromNow;
    };

    const fetchTasksWithAttachments = async () => {
        // If preFilteredTasks are provided, use them directly
        if (preFilteredTasks) {
            const tasksWithAttachments = preFilteredTasks.map(task => ({
                ...task,
                image_count: (task as any).image_count || 0,
                document_count: (task as any).document_count || 0,
                total_attachments: (task as any).total_attachments ||
                    ((task as any).image_count || 0) + ((task as any).document_count || 0),
            } as TaskWithAttachments));

            setTasks(tasksWithAttachments);
            setLoading(false);
            return;
        }

        performanceMonitor.start('fetchTasksWithAttachments');
        setLoading(true);

        try {
            performanceMonitor.start('fetchInitialTasks');
            let response: Task[];

            if (showArchived) {
                const allTasks = await TasksService.getTasksTaskGet(undefined, undefined, undefined, undefined, undefined, true);
                response = allTasks.filter(task => {
                    const isArchived = task.status === TaskStatus.ARCHIVED;
                    if (showOnlyAssigned) {
                        return isArchived && (
                            task.assigned_to === currentUserId ||
                            task.assigned_to === email ||
                            task.created_by === currentUserId ||
                            task.created_by === email
                        );
                    }
                    return isArchived;
                });
            } else {
                response = showOnlyAssigned
                    ? await TasksService.getMyTasksTaskMeGet()
                    : await TasksService.getTasksTaskGet();
            }

            performanceMonitor.end('fetchInitialTasks');

            // Filter by status if filterByStatus is provided
            if (filterByStatus) {
                response = response.filter(task => task.status === filterByStatus);
            }

            // Use attachment counts from backend if available, otherwise default to 0
            const tasksInitial = response.map(task => ({
                ...task,
                // Check if backend provides attachment counts
                image_count: (task as any).image_count || 0,
                document_count: (task as any).document_count || 0,
                total_attachments: (task as any).total_attachments ||
                    ((task as any).image_count || 0) + ((task as any).document_count || 0),
            } as TaskWithAttachments));

            setTasks(tasksInitial);
            setError(null);
            setLoading(false);

            // NOTE: Removed attachment loading for performance
            // Attachments are now loaded only when task detail is opened

        } catch (err) {
            console.error('Error details:', err);
            setError('Failed to fetch tasks');
            setLoading(false);
        } finally {
            performanceMonitor.end('fetchTasksWithAttachments');
        }
    };

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchTasksWithAttachments();
    }, [onTaskCreated, showOnlyAssigned, showArchived, filterByStatus, preFilteredTasks]);

    const loadBauvorhaben = async () => {
        try {
            const bv = await ArbeitszeitService.getAlleBauvorhabenArbeitzeitBauvorhabenGet(false);
            setBauvorhaben(bv as unknown as BauvorhabenWithID[]);
        } catch (error) {
            console.error('Error loading Bauvorhaben:', error);
        }
    };

    useEffect(() => {
        loadBauvorhaben();
    }, []);

    // Auto-open task if specified in URL
    useEffect(() => {
        if (autoOpenTaskId && tasks.length > 0 && !selectedTaskId) {
            const taskExists = tasks.some(task => task.id === autoOpenTaskId);
            if (taskExists) {
                setSelectedTaskId(autoOpenTaskId);

                TasksService.logTaskOpenedTaskTaskIdOpenPost(autoOpenTaskId)
                    .then(() => {
                        console.log('[DEBUG task-list.tsx] Auto-opened task logged successfully:', autoOpenTaskId);
                    })
                    .catch((error) => {
                        console.error('[DEBUG task-list.tsx] Failed to log auto-opened task:', error);
                        console.error('[DEBUG task-list.tsx] Error details:', {
                            message: error.message,
                            stack: error.stack,
                            response: error.response
                        });
                    });

                if (onAutoOpenComplete) {
                    onAutoOpenComplete();
                } else {
                    console.log('[DEBUG task-list.tsx] No onAutoOpenComplete callback provided');
                }
            } else {
                console.log('[DEBUG task-list.tsx] Task not found in current list. Available task IDs:',
                    tasks.map(t => t.id).join(', '));
            }
        } else {
            if (!autoOpenTaskId) {
                console.log('[DEBUG task-list.tsx] No autoOpenTaskId provided');
            }
            if (tasks.length === 0) {
                console.log('[DEBUG task-list.tsx] Tasks not loaded yet');
            }
            if (selectedTaskId) {
                console.log('[DEBUG task-list.tsx] Task already selected:', selectedTaskId);
            }
        }
    }, [autoOpenTaskId, tasks, selectedTaskId, onAutoOpenComplete]);

    const getBauvorhabenName = (bauvorhabenId: number | undefined | null, projektName?: string) => {
        if (!bauvorhabenId) return projektName || 'Kein Bauvorhaben';
        const bvh = bauvorhaben.find(b => b.id === bauvorhabenId);
        return bvh?.bvh_name || 'Unbekanntes Bauvorhaben';
    };

    // Get all unique assigned users from tasks
    const getUniqueAssignedUsers = useMemo(() => {
        const uniqueUsers = new Map<string, { name: string; id: string }>();

        tasks.forEach(task => {
            if (task.assigned_to && task.assigned_to_name) {
                // Use assigned_to as ID and assigned_to_name as display name
                uniqueUsers.set(task.assigned_to, {
                    id: task.assigned_to,
                    name: task.assigned_to_name
                });
            }
        });

        // Convert to array and sort by name
        return Array.from(uniqueUsers.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [tasks]);

    const handleSort = useCallback((field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setShowSortOptions(false);
    }, [sortField, sortDirection]);

    const filteredAndSortedTasks = useMemo(() => {
        let filtered = tasks.filter(task => {
            // Search filter
            if (debouncedSearchTerm) {
                const search = debouncedSearchTerm.toLowerCase();
                const matchesSearch =
                    task.title.toLowerCase().includes(search) ||
                    task.description?.toLowerCase().includes(search) ||
                    task.assigned_to_name?.toLowerCase().includes(search) ||
                    task.created_by_name?.toLowerCase().includes(search) ||
                    getBauvorhabenName(task.bauvorhaben_id, task.projekt_name).toLowerCase().includes(search) ||
                    task.id.toString().includes(search);

                if (!matchesSearch) return false;
            }

            // Status filter
            if (filters.status !== 'ALL' && task.status !== filters.status) {
                return false;
            }

            // Assigned to filter
            if (filters.assignedTo !== 'ALL') {
                if (filters.assignedTo === 'ME') {
                    if (task.assigned_to !== currentUserId && task.assigned_to !== email) {
                        return false;
                    }
                } else if (filters.assignedTo === 'UNASSIGNED') {
                    if (task.assigned_to) {
                        return false;
                    }
                } else {
                    // Filter by specific user ID
                    if (task.assigned_to !== filters.assignedTo) {
                        return false;
                    }
                }
            }

            // Priority filter
            if (filters.priority !== 'ALL' && task.due_date) {
                if (filters.priority === 'OVERDUE') {
                    if (!isOverdue(task.due_date) || task.status === TaskStatus.COMPLETED) {
                        return false;
                    }
                } else if (filters.priority === 'DUE_SOON') {
                    if (!isDueSoon(task.due_date) || task.status === TaskStatus.COMPLETED) {
                        return false;
                    }
                }
            } else if (filters.priority === 'NO_DUE_DATE') {
                if (task.due_date) {
                    return false;
                }
            }

            // Attachments filter
            if (filters.hasAttachments !== 'ALL') {
                if (filters.hasAttachments === 'WITH_ATTACHMENTS' && (task.total_attachments || 0) === 0) {
                    return false;
                } else if (filters.hasAttachments === 'WITHOUT_ATTACHMENTS' && (task.total_attachments || 0) > 0) {
                    return false;
                }
            }

            // Bauvorhaben filter
            if (filters.bauvorhaben_id !== 'ALL') {
                if (task.bauvorhaben_id !== filters.bauvorhaben_id) {
                    return false;
                }
            }

            return true;
        });

        // Separate sorting logic for better performance
        return filtered.sort((a, b) => {
            // Status priority: OPEN -> IN_PROGRESS -> BLOCKED -> COMPLETED
            const getStatusPriority = (status?: TaskStatus) => {
                switch (status) {
                    case TaskStatus.OPEN:
                        return 1;
                    case TaskStatus.IN_PROGRESS:
                        return 2;
                    case TaskStatus.BLOCKED:
                        return 3;
                    case TaskStatus.COMPLETED:
                        return 4;
                    case TaskStatus.ARCHIVED:
                        return 5;
                    default:
                        return 1; // Default to OPEN priority
                }
            };

            const aStatusPriority = getStatusPriority(a.status);
            const bStatusPriority = getStatusPriority(b.status);

            if (aStatusPriority !== bStatusPriority) {
                return aStatusPriority - bStatusPriority;
            }

            // Priority sorting: assigned to me first (within same status)
            const aAssignedToMe = a.assigned_to === currentUserId || a.assigned_to === email;
            const bAssignedToMe = b.assigned_to === currentUserId || b.assigned_to === email;

            if (aAssignedToMe && !bAssignedToMe) return -1;
            if (!aAssignedToMe && bAssignedToMe) return 1;

            // Due date priority
            const aHasDueDate = !!a.due_date;
            const bHasDueDate = !!b.due_date;

            if (aHasDueDate && !bHasDueDate) return -1;
            if (!aHasDueDate && bHasDueDate) return 1;

            // Overdue tasks first
            if (aHasDueDate && bHasDueDate) {
                const aDueTime = new Date(a.due_date!).getTime();
                const bDueTime = new Date(b.due_date!).getTime();
                const now = new Date().getTime();

                const aOverdue = aDueTime < now;
                const bOverdue = bDueTime < now;

                if (aOverdue && !bOverdue) return -1;
                if (!aOverdue && bOverdue) return 1;

                if (aDueTime !== bDueTime) {
                    return aDueTime - bDueTime;
                }
            }

            // Custom sort field
            let aValue: any = a[sortField];
            let bValue: any = b[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
            if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

            if (sortField === 'created_at' || sortField === 'due_date') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [
        tasks,
        debouncedSearchTerm,
        filters.status,
        filters.assignedTo,
        filters.priority,
        filters.hasAttachments,
        filters.bauvorhaben_id,
        sortField,
        sortDirection,
        currentUserId,
        email
    ]);

    const getStatusColor = (status?: TaskStatus) => {
        switch (status) {
            case TaskStatus.COMPLETED:
                return 'success';
            case TaskStatus.IN_PROGRESS:
                return 'primary';
            case TaskStatus.BLOCKED:
                return 'error';
            case TaskStatus.ARCHIVED:
                return 'default';
            default:
                return 'warning';
        }
    };

    const translateStatus = (status?: TaskStatus) => {
        switch (status) {
            case TaskStatus.COMPLETED:
                return 'Abgeschlossen';
            case TaskStatus.IN_PROGRESS:
                return 'In Bearbeitung';
            case TaskStatus.BLOCKED:
                return 'Blockiert';
            case TaskStatus.OPEN:
                return 'Offen';
            case TaskStatus.ARCHIVED:
                return 'Archiviert';
            default:
                return 'Offen';
        }
    };

    const getSortFieldLabel = (field: SortField) => {
        switch (field) {
            case 'title':
                return 'Titel';
            case 'status':
                return 'Status';
            case 'assigned_to_name':
                return 'Zugewiesen an';
            case 'created_at':
                return 'Erstellt am';
            case 'due_date':
                return 'Fälligkeitsdatum';
            case 'total_attachments':
                return 'Anhänge';
            default:
                return field;
        }
    };

    const handleRestoreTask = useCallback(async (taskId: number) => {
        try {
            await TasksService.restoreTaskTaskTaskIdRestorePost(taskId, {
                new_status: TaskStatus.OPEN
            });
            fetchTasksWithAttachments();
        } catch (error) {
            console.error('Error restoring task:', error);
        }
    }, []);

    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setFilters({
            status: 'ALL',
            assignedTo: 'ALL',
            priority: 'ALL',
            hasAttachments: 'ALL',
            bauvorhaben_id: 'ALL',
        });
    }, []);

    const handleTaskClick = useCallback((taskId: number) => {
        setSelectedTaskId(taskId);

        // Log task opened event
        TasksService.logTaskOpenedTaskTaskIdOpenPost(taskId)
            .then(() => {
                console.log('Task opened event logged:', taskId);
            })
            .catch((error) => {
                console.error('Failed to log task opened event:', error);
            });
    }, []);

    const getTaskIcon = (task: TaskWithAttachments) => {
        const assignedToMe = task.assigned_to === currentUserId || task.assigned_to === email;
        let backgroundColor = '#f5f5f5';
        let color = '#666';

        if (assignedToMe) {
            backgroundColor = '#e3f2fd';
            color = '#1976d2';
        }

        if (task.due_date && task.status !== TaskStatus.COMPLETED) {
            if (isOverdue(task.due_date)) {
                backgroundColor = '#ffcdd2';
                color = '#d32f2f';
            } else if (isDueSoon(task.due_date)) {
                backgroundColor = '#ffe0b2';
                color = '#f57c00';
            }
        }

        return (
            <Avatar sx={{backgroundColor, color}}>
                <TaskIcon/>
            </Avatar>
        );
    };

    const MobileTaskItem = memo(({task, index, onTaskClick, onRestoreTask, showArchived}: {
        task: TaskWithAttachments;
        index: number;
        onTaskClick: (taskId: number) => void;
        onRestoreTask: (taskId: number) => void;
        showArchived: boolean;
    }) => (
        <Card
            key={task.id}
            sx={{
                mb: 1,
                borderRadius: 2,
                boxShadow: 1,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-1px)',
                }
            }}
        >
            <ListItem
                onClick={() => onTaskClick(task.id)}
                sx={{
                    cursor: 'pointer',
                    p: 1.5,
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    }
                }}
            >
                <ListItemText
                    primary={
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5}}>
                            <Typography
                                variant="body1"
                                fontWeight="800"
                                sx={{
                                    fontSize: '15px',
                                    color: 'text.primary',
                                    lineHeight: 1.2,
                                    pr: 1
                                }}
                            >
                                {task.title.length > 40 ? `${task.title.substring(0, 40)}...` : task.title}
                            </Typography>
                            <ArrowRightIcon sx={{color: 'text.secondary', fontSize: '16px'}}/>
                        </Box>
                    }
                    secondary={
                        <Stack spacing={0.5}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap'}}>
                                <Chip
                                    label={translateStatus(task.status)}
                                    color={getStatusColor(task.status)}
                                    size="small"
                                    variant="outlined"
                                />
                                {(task.total_attachments || 0) > 0 && (
                                    <Tooltip title="Anhänge werden beim Öffnen der Aufgabe geladen">
                                        <Badge
                                            badgeContent={task.total_attachments}
                                            color="primary"
                                        >
                                            <AttachFileIcon sx={{color: 'text.secondary', fontSize: '14px'}}/>
                                        </Badge>
                                    </Tooltip>
                                )}
                            </Box>

                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                                {task.assigned_to_name && (
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
                                        <PersonIcon sx={{color: 'text.secondary', fontSize: '12px'}}/>
                                        <Typography
                                            variant="caption"
                                            sx={{fontSize: '11px', color: 'text.secondary'}}
                                        >
                                            {task.assigned_to_name.length > 15 ? `${task.assigned_to_name.substring(0, 15)}...` : task.assigned_to_name}
                                        </Typography>
                                    </Box>
                                )}
                                {task.projekt_name || task.bauvorhaben_id && (
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
                                        <BusinessIcon sx={{color: 'text.secondary', fontSize: '12px'}}/>
                                        <Typography
                                            variant="caption"
                                            sx={{fontSize: '11px', color: 'text.secondary'}}
                                        >
                                            {getBauvorhabenName(task.bauvorhaben_id, task.projekt_name)}
                                        </Typography>
                                    </Box>
                                )}

                                {task.due_date && (
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
                                        <CalendarIcon
                                            sx={{
                                                color: task.status !== TaskStatus.COMPLETED
                                                    ? isOverdue(task.due_date)
                                                        ? 'error.main'
                                                        : isDueSoon(task.due_date)
                                                            ? 'warning.main'
                                                            : 'text.secondary'
                                                    : 'text.secondary',
                                                fontSize: '12px'
                                            }}
                                        />
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '11px',
                                                color: task.status !== TaskStatus.COMPLETED
                                                    ? isOverdue(task.due_date)
                                                        ? 'error.main'
                                                        : isDueSoon(task.due_date)
                                                            ? 'warning.main'
                                                            : 'text.secondary'
                                                    : 'text.secondary',
                                                fontWeight: (task.status !== TaskStatus.COMPLETED &&
                                                    (isOverdue(task.due_date) || isDueSoon(task.due_date))) ? 600 : 400
                                            }}
                                        >
                                            {formatDate(task.due_date)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Stack>
                    }
                />
                {showArchived && (
                    <ListItemSecondaryAction sx={{right: 8}}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRestoreTask(task.id);
                            }}
                            sx={{
                                backgroundColor: 'primary.light',
                                color: 'primary.main',
                                width: '32px',
                                height: '32px',
                                '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'white'
                                }
                            }}
                        >
                            <RestoreIcon sx={{fontSize: '16px'}}/>
                        </IconButton>
                    </ListItemSecondaryAction>
                )}
            </ListItem>
        </Card>
    ));

    const renderMobileView = () => (
        <Box sx={{px: 0}}>
            {sortField === 'status' ? (
                // Group tasks by status when sorting by status
                (() => {
                    const groupedTasks = filteredAndSortedTasks.reduce((acc, task) => {
                        const status = task.status || TaskStatus.OPEN;
                        if (!acc[status]) {
                            acc[status] = [];
                        }
                        acc[status].push(task);
                        return acc;
                    }, {} as Record<TaskStatus, typeof filteredAndSortedTasks>);

                    const statusOrder = [TaskStatus.OPEN, TaskStatus.IN_PROGRESS, TaskStatus.BLOCKED, TaskStatus.COMPLETED, TaskStatus.ARCHIVED];

                    return statusOrder.flatMap(status => {
                        const tasks = groupedTasks[status];
                        if (!tasks || tasks.length === 0) return [];

                        return [
                            // Status divider
                            <Box key={`divider-${status}`} sx={{
                                mt: 2,
                                mb: 1,
                                p: 2,
                                backgroundColor: 'grey.100',
                                borderRadius: 1,
                                borderTop: '3px solid',
                                borderTopColor: getStatusColor(status) === 'success' ? 'success.main' :
                                    getStatusColor(status) === 'error' ? 'error.main' :
                                        getStatusColor(status) === 'warning' ? 'warning.main' :
                                            'primary.main'
                            }}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Chip
                                        label={translateStatus(status)}
                                        color={getStatusColor(status)}
                                        size="small"
                                        variant="filled"
                                    />
                                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                                        {tasks.length} {tasks.length === 1 ? 'Aufgabe' : 'Aufgaben'}
                                    </Typography>
                                </Box>
                            </Box>,
                            // Task cards
                            ...tasks.map((task, index) => (
                                <MobileTaskItem
                                    key={task.id}
                                    task={task}
                                    index={index}
                                    onTaskClick={handleTaskClick}
                                    onRestoreTask={handleRestoreTask}
                                    showArchived={showArchived}
                                />
                            ))
                        ];
                    });
                })()
            ) : (
                // Regular task cards when not sorting by status
                filteredAndSortedTasks.map((task, index) => (
                    <MobileTaskItem
                        key={task.id}
                        task={task}
                        index={index}
                        onTaskClick={handleTaskClick}
                        onRestoreTask={handleRestoreTask}
                        showArchived={showArchived}
                    />
                ))
            )}
        </Box>
    );

    const DesktopTaskRow = memo(({task, onTaskClick, onRestoreTask, showArchived}: {
        task: TaskWithAttachments;
        onTaskClick: (taskId: number) => void;
        onRestoreTask: (taskId: number) => void;
        showArchived: boolean;
    }) => (
        <TableRow
            key={task.id}
            onClick={() => onTaskClick(task.id)}
            sx={{
                cursor: 'pointer',
            }}
            hover
        >
            <TableCell>
                <Box>
                    <Typography variant='body2' fontWeight="medium">
                        {task.title}
                    </Typography>
                    {task.description && (
                        <Typography variant='caption' color='text.secondary' sx={{display: 'block'}}>
                            {task.description.length > 100
                                ? `${task.description.substring(0, 100)}...`
                                : task.description
                            }
                        </Typography>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <Typography variant='body2'>
                    {getBauvorhabenName(task.bauvorhaben_id, task.projekt_name)}
                </Typography>
            </TableCell>
            <TableCell>
                <Chip
                    label={translateStatus(task.status)}
                    color={getStatusColor(task.status)}
                    size='small'
                    variant='outlined'
                />
            </TableCell>
            <TableCell>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Typography variant='body2'>
                        {task.assigned_to_name || 'Nicht zugewiesen'}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    {(task.total_attachments || 0) > 0 ? (
                        <Tooltip title="Anhänge werden beim Öffnen der Aufgabe geladen">
                            <Badge badgeContent={task.total_attachments} color="primary">
                                <AttachFileIcon fontSize="small"/>
                            </Badge>
                        </Tooltip>
                    ) : (
                        <Typography variant='body2' color='text.secondary'>
                            -
                        </Typography>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <Typography variant='body2'>
                    {formatDate(task.created_at)}
                </Typography>
            </TableCell>
            <TableCell>
                <Box>
                    <Typography
                        variant='body2'
                        color={
                            task.due_date && task.status !== TaskStatus.COMPLETED
                                ? isOverdue(task.due_date)
                                    ? 'error'
                                    : isDueSoon(task.due_date)
                                        ? 'warning.main'
                                        : 'text.primary'
                                : 'text.primary'
                        }
                    >
                        {task.due_date ? formatDate(task.due_date) : '-'}
                    </Typography>
                </Box>
            </TableCell>
            {showArchived && (
                <TableCell>
                    <Tooltip title="Task wiederherstellen">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRestoreTask(task.id);
                            }}
                        >
                            <RestoreIcon/>
                        </IconButton>
                    </Tooltip>
                </TableCell>
            )}
        </TableRow>
    ));

    const renderDesktopView = () => (
        <Box sx={{px: 1}}>
            <TableContainer style={{}} component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'title'}
                                    direction={sortField === 'title' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('title')}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <TaskIcon fontSize="small" fontWeight="600"/>
                                        Titel
                                    </Box>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <BusinessIcon fontSize="small"/>
                                    Bauvorhaben
                                </Box>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'status'}
                                    direction={sortField === 'status' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('status')}
                                >
                                    Status
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'assigned_to_name'}
                                    direction={sortField === 'assigned_to_name' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('assigned_to_name')}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <PersonIcon fontSize="small"/>
                                        Zugewiesen an
                                    </Box>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'total_attachments'}
                                    direction={sortField === 'total_attachments' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('total_attachments')}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <AttachFileIcon fontSize="small"/>
                                        Anhänge
                                    </Box>
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'created_at'}
                                    direction={sortField === 'created_at' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('created_at')}
                                >
                                    Erstellt am
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                <TableSortLabel
                                    active={sortField === 'due_date'}
                                    direction={sortField === 'due_date' ? sortDirection : 'asc'}
                                    onClick={() => handleSort('due_date')}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <CalendarIcon fontSize="small"/>
                                        Fälligkeitsdatum
                                    </Box>
                                </TableSortLabel>
                            </TableCell>
                            {showArchived && (
                                <TableCell>Aktionen</TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortField === 'status' ? (
                            // Group tasks by status when sorting by status
                            (() => {
                                const groupedTasks = filteredAndSortedTasks.reduce((acc, task) => {
                                    const status = task.status || TaskStatus.OPEN;
                                    if (!acc[status]) {
                                        acc[status] = [];
                                    }
                                    acc[status].push(task);
                                    return acc;
                                }, {} as Record<TaskStatus, typeof filteredAndSortedTasks>);

                                const statusOrder = [TaskStatus.OPEN, TaskStatus.IN_PROGRESS, TaskStatus.BLOCKED, TaskStatus.COMPLETED, TaskStatus.ARCHIVED];

                                return statusOrder.flatMap(status => {
                                    const tasks = groupedTasks[status];
                                    if (!tasks || tasks.length === 0) return [];

                                    return [
                                        // Status divider row
                                        <TableRow key={`divider-${status}`}>
                                            <TableCell
                                                colSpan={showArchived ? 8 : 7}
                                                sx={{
                                                    backgroundColor: 'grey.100',
                                                    borderTop: '2px solid',
                                                    borderTopColor: 'divider',
                                                    py: 1,
                                                    fontWeight: 600
                                                }}
                                            >
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <Chip
                                                        label={translateStatus(status)}
                                                        color={getStatusColor(status)}
                                                        size="small"
                                                        variant="filled"
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        {tasks.length} {tasks.length === 1 ? 'Aufgabe' : 'Aufgaben'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>,
                                        // Task rows
                                        ...tasks.map((task) => (
                                            <DesktopTaskRow
                                                key={task.id}
                                                task={task}
                                                onTaskClick={handleTaskClick}
                                                onRestoreTask={handleRestoreTask}
                                                showArchived={showArchived}
                                            />
                                        ))
                                    ];
                                });
                            })()
                        ) : (
                            // Regular task rows when not sorting by status
                            filteredAndSortedTasks.map((task) => (
                                <DesktopTaskRow
                                    key={task.id}
                                    task={task}
                                    onTaskClick={handleTaskClick}
                                    onRestoreTask={handleRestoreTask}
                                    showArchived={showArchived}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity='error' sx={{m: 2}}>
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            {/* Search and Filter Section */}
            {!hideFilters && (
                <Card
                    sx={{
                        mb: 2,
                        borderRadius: 'var(--border-radius-lg)',
                        boxShadow: 'var(--shadow-2)',
                        border: '1px solid var(--border-color)',
                        backgroundColor: 'var(--background-paper)',
                    }}
                >
                    <CardContent sx={{}}>
                        <Stack spacing="var(--spacing)">
                            {/* Search with Filter Button */}
                            <Box sx={{display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'stretch'}}>
                                <TextField
                                    fullWidth
                                    placeholder="Suche…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="medium"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{color: 'var(--text-secondary)'}}/>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: 'var(--border-radius-lg)',
                                            fontSize: '14px',
                                            fontFamily: 'var(--font-family-primary)',
                                            minHeight: '48px',
                                            backgroundColor: 'var(--background-paper)',
                                            border: '1px solid var(--border-color)',
                                            '&:hover': {
                                                borderColor: 'var(--primary-main)',
                                            },
                                            '&.Mui-focused': {
                                                borderColor: 'var(--primary-main)',
                                                boxShadow: '0 0 0 3px color-mix(in srgb, var(--primary-main) 10%, transparent)',
                                            }
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                borderColor: '#E2E8F0',
                                            },
                                            '&:hover fieldset': {},
                                            '&.Mui-focused fieldset': {
                                                borderColor: 'var(--primary-main)',
                                            }
                                        }
                                    }}
                                />
                                <IconButton
                                    onClick={() => setShowFilters(!showFilters)}
                                    sx={{
                                        width: '48px',
                                        height: '48px',
                                        borderRadius: 'var(--border-radius-lg)',
                                        border: '1px solid var(--border-color)',
                                        backgroundColor: showFilters ? 'var(--primary-main)' : 'var(--background-paper)',
                                        color: showFilters ? 'var(--background-paper)' : 'var(--text-secondary)',
                                        flexShrink: 0,
                                        alignSelf: 'stretch',
                                        '&:hover': {
                                            backgroundColor: showFilters ? 'var(--primary-dark)' : 'var(--background-level1)',
                                            borderColor: 'var(--primary-main)',
                                        }
                                    }}
                                >
                                    <FilterIcon/>
                                </IconButton>
                            </Box>
                            <Box sx={{mb: '16px'}}>
                                <Typography variant="body2" sx={{mb: 1, color: 'text.secondary'}}>
                                    Status:
                                </Typography>
                                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                                    <Chip
                                        label="Alle"
                                        variant={filters.status === 'ALL' ? 'filled' : 'outlined'}
                                        color={filters.status === 'ALL' ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => setFilters(prev => ({...prev, status: 'ALL'}))}
                                        clickable
                                    />
                                    <Chip
                                        label="Offen"
                                        variant={filters.status === TaskStatus.OPEN ? 'filled' : 'outlined'}
                                        color={filters.status === TaskStatus.OPEN ? 'warning' : 'default'}
                                        size="small"
                                        onClick={() => setFilters(prev => ({...prev, status: TaskStatus.OPEN}))}
                                        clickable
                                    />
                                    <Chip
                                        label="In Bearbeitung"
                                        variant={filters.status === TaskStatus.IN_PROGRESS ? 'filled' : 'outlined'}
                                        color={filters.status === TaskStatus.IN_PROGRESS ? 'primary' : 'default'}
                                        size="small"
                                        onClick={() => setFilters(prev => ({...prev, status: TaskStatus.IN_PROGRESS}))}
                                        clickable
                                    />
                                    <Chip
                                        label="Abgeschlossen"
                                        variant={filters.status === TaskStatus.COMPLETED ? 'filled' : 'outlined'}
                                        color={filters.status === TaskStatus.COMPLETED ? 'success' : 'default'}
                                        size="small"
                                        onClick={() => setFilters(prev => ({...prev, status: TaskStatus.COMPLETED}))}
                                        clickable
                                    />
                                    <Chip
                                        label="Blockiert"
                                        variant={filters.status === TaskStatus.BLOCKED ? 'filled' : 'outlined'}
                                        color={filters.status === TaskStatus.BLOCKED ? 'error' : 'default'}
                                        size="small"
                                        onClick={() => setFilters(prev => ({...prev, status: TaskStatus.BLOCKED}))}
                                        clickable
                                    />
                                    {showArchived && (
                                        <Chip
                                            label="Archiviert"
                                            variant={filters.status === TaskStatus.ARCHIVED ? 'filled' : 'outlined'}
                                            color={filters.status === TaskStatus.ARCHIVED ? 'default' : 'default'}
                                            size="small"
                                            onClick={() => setFilters(prev => ({...prev, status: TaskStatus.ARCHIVED}))}
                                            clickable
                                        />
                                    )}
                                </Box>
                            </Box>
                            {/* Expanded Filter and Sort Options */}
                            <Collapse in={showFilters}>
                                <Stack spacing="var(--spacing-lg)">
                                    {/* Mobile Controls */}
                                    {isMobile && (
                                        <Grid container spacing="var(--spacing-xs)" alignItems="center">
                                            <Grid item xs={6}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    onClick={clearFilters}
                                                >
                                                    Reset
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )}

                                    {/* Desktop Controls */}
                                    {!isMobile && (
                                        <Grid container spacing="var(--spacing)" alignItems="center">
                                            <Grid item xs={12} md={6}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'var(--text-secondary)',
                                                        fontSize: '13px',
                                                        fontFamily: 'var(--font-family-primary)'
                                                    }}
                                                >
                                                    Sortiert nach: {getSortFieldLabel(sortField)} ({sortDirection === 'asc' ? 'Aufsteigend' : 'Absteigend'})
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    onClick={clearFilters}
                                                >
                                                    Filter zurücksetzen
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {/* Mobile Sort Options */}
                                    <Collapse in={showSortOptions && isMobile}>
                                        <Box
                                            sx={{
                                                p: 'var(--spacing-lg)',
                                                backgroundColor: 'var(--background-level1)',
                                                borderRadius: 'var(--border-radius-lg)',
                                                border: '1px solid var(--border-color)'
                                            }}
                                        >
                                            <Grid container spacing="10px">
                                                {[
                                                    {field: 'due_date', label: 'Fälligkeitsdatum'},
                                                    {field: 'title', label: 'Titel'},
                                                    {field: 'status', label: 'Status'},
                                                    {field: 'created_at', label: 'Erstellt am'},
                                                    {field: 'total_attachments', label: 'Anhänge'},
                                                ].map(({field, label}) => (
                                                    <Grid item xs={6} key={field}>
                                                        <Button
                                                            fullWidth
                                                            size="large"
                                                            variant={sortField === field ? "contained" : "outlined"}
                                                            onClick={() => handleSort(field as SortField)}
                                                            sx={{
                                                                borderRadius: '14px',
                                                                minHeight: '48px',
                                                                fontWeight: 600,
                                                                fontSize: '14px',
                                                                fontFamily: 'Inter, system-ui, sans-serif',
                                                                textTransform: 'none',
                                                                ...(sortField === field
                                                                        ? {
                                                                            backgroundColor: '#2563EB',
                                                                            color: 'white',
                                                                            boxShadow: '0 6px 20px rgba(2, 6, 23, 0.08)',
                                                                            '&:hover': {
                                                                                backgroundColor: '#1D4ED8',
                                                                            }
                                                                        }
                                                                        : {
                                                                            borderColor: '#E2E8F0',
                                                                            color: '#0F172A',
                                                                            backgroundColor: '#FFFFFF',
                                                                            '&:hover': {
                                                                                backgroundColor: '#F8FAFC',
                                                                                borderColor: '#2563EB',
                                                                            }
                                                                        }
                                                                )
                                                            }}
                                                        >
                                                            {label}
                                                            {sortField === field && (
                                                                <Box
                                                                    component="span"
                                                                    sx={{
                                                                        ml: 1,
                                                                        fontSize: '16px',
                                                                        fontWeight: 'bold'
                                                                    }}
                                                                >
                                                                    {sortDirection === 'asc' ? '↑' : '↓'}
                                                                </Box>
                                                            )}
                                                        </Button>
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    </Collapse>
                                    {/* Filter Options - Always visible when expanded */}
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            sx={{
                                                mb: '12px',
                                                fontWeight: 600,
                                                color: 'var(--text-primary)',
                                                fontSize: isMobile ? '18px' : '16px',
                                                fontFamily: 'var(--font-family-primary)'
                                            }}
                                        >
                                            Filter-Optionen
                                        </Typography>

                                        {/* Quick Status Filter Chips */}

                                        <Grid container spacing={isMobile ? 3 : 2}>
                                            {!filterByStatus && (
                                                <Grid item xs={12} sm={6} md={3}>
                                                    <FormControl
                                                        fullWidth
                                                        size={isMobile ? "medium" : "small"}
                                                        sx={{
                                                            '& .MuiInputLabel-root': {
                                                                fontSize: isMobile ? '16px' : '14px',
                                                                fontFamily: 'var(--font-family-primary)',
                                                                color: 'var(--text-secondary)',
                                                            },
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 'var(--border-radius-lg)',
                                                                backgroundColor: 'var(--background-paper)',
                                                                '& fieldset': {
                                                                    borderColor: 'var(--border-color)',
                                                                },
                                                                '&:hover fieldset': {
                                                                    borderColor: 'var(--primary-main)',
                                                                },
                                                                '&.Mui-focused fieldset': {
                                                                    borderColor: 'var(--primary-main)',
                                                                }
                                                            }
                                                        }}
                                                    >
                                                        <InputLabel sx={{fontSize: isMobile ? '16px' : '14px', fontFamily: 'var(--font-family-primary)'}}>Status</InputLabel>
                                                        <Select
                                                            value={filters.status}
                                                            label="Status"
                                                            onChange={(e) => setFilters(prev => ({...prev, status: e.target.value as any}))}
                                                            sx={{
                                                                minHeight: isMobile ? '48px' : '40px',
                                                                fontSize: isMobile ? '16px' : '14px',
                                                                fontFamily: 'var(--font-family-primary)',
                                                            }}
                                                        >
                                                            <MenuItem value="ALL">Alle Status</MenuItem>
                                                            <MenuItem value={TaskStatus.OPEN}>Offen</MenuItem>
                                                            <MenuItem value={TaskStatus.IN_PROGRESS}>In Bearbeitung</MenuItem>
                                                            <MenuItem value={TaskStatus.COMPLETED}>Abgeschlossen</MenuItem>
                                                            <MenuItem value={TaskStatus.BLOCKED}>Blockiert</MenuItem>
                                                            {showArchived && <MenuItem value={TaskStatus.ARCHIVED}>Archiviert</MenuItem>}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            )}

                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl
                                                    fullWidth
                                                    size={isMobile ? "medium" : "small"}
                                                    sx={{
                                                        '& .MuiInputLabel-root': {
                                                            fontSize: isMobile ? '16px' : '14px',
                                                            fontFamily: 'var(--font-family-primary)',
                                                            color: 'var(--text-secondary)',
                                                        },
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 'var(--border-radius-lg)',
                                                            backgroundColor: 'var(--background-paper)',
                                                            '& fieldset': {
                                                                borderColor: 'var(--border-color)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <InputLabel sx={{fontSize: isMobile ? '16px' : '14px', fontFamily: 'var(--font-family-primary)'}}>Bauvorhaben</InputLabel>
                                                    <Select
                                                        value={filters.bauvorhaben_id}
                                                        label="Bauvorhaben"
                                                        onChange={(e) => setFilters(prev => ({...prev, bauvorhaben_id: e.target.value as any}))}
                                                        sx={{
                                                            minHeight: isMobile ? '48px' : '40px',
                                                            fontSize: isMobile ? '16px' : '14px',
                                                            fontFamily: 'var(--font-family-primary)',
                                                        }}
                                                    >
                                                        <MenuItem value="ALL">Alle</MenuItem>
                                                        {bauvorhaben.map((bvh) => (
                                                            <MenuItem key={bvh.id} value={bvh.id}>
                                                                {bvh.bvh_name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl
                                                    fullWidth
                                                    size={isMobile ? "medium" : "small"}
                                                    sx={{
                                                        '& .MuiInputLabel-root': {
                                                            fontSize: isMobile ? '16px' : '14px',
                                                            fontFamily: 'var(--font-family-primary)',
                                                            color: 'var(--text-secondary)',
                                                        },
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 'var(--border-radius-lg)',
                                                            backgroundColor: 'var(--background-paper)',
                                                            '& fieldset': {
                                                                borderColor: 'var(--border-color)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <InputLabel sx={{fontSize: isMobile ? '16px' : '14px', fontFamily: 'var(--font-family-primary)'}}>Priorität</InputLabel>
                                                    <Select
                                                        value={filters.priority}
                                                        label="Priorität"
                                                        onChange={(e) => setFilters(prev => ({...prev, priority: e.target.value as any}))}
                                                        sx={{
                                                            minHeight: isMobile ? '48px' : '40px',
                                                            fontSize: isMobile ? '16px' : '14px',
                                                            fontFamily: 'var(--font-family-primary)',
                                                        }}
                                                    >
                                                        <MenuItem value="ALL">Alle</MenuItem>
                                                        <MenuItem value="OVERDUE">Überfällig</MenuItem>
                                                        <MenuItem value="DUE_SOON">Bald fällig</MenuItem>
                                                        <MenuItem value="NO_DUE_DATE">Ohne Fälligkeitsdatum</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl
                                                    fullWidth
                                                    size={isMobile ? "medium" : "small"}
                                                    sx={{
                                                        '& .MuiInputLabel-root': {
                                                            fontSize: isMobile ? '16px' : '14px',
                                                            fontFamily: 'var(--font-family-primary)',
                                                            color: 'var(--text-secondary)',
                                                        },
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 'var(--border-radius-lg)',
                                                            backgroundColor: 'var(--background-paper)',
                                                            '& fieldset': {
                                                                borderColor: 'var(--border-color)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <InputLabel sx={{fontSize: isMobile ? '16px' : '14px', fontFamily: 'var(--font-family-primary)'}}>Anhänge</InputLabel>
                                                    <Select
                                                        value={filters.hasAttachments}
                                                        label="Anhänge"
                                                        onChange={(e) => setFilters(prev => ({...prev, hasAttachments: e.target.value as any}))}
                                                        sx={{
                                                            minHeight: isMobile ? '48px' : '40px',
                                                            fontSize: isMobile ? '16px' : '14px',
                                                            fontFamily: 'var(--font-family-primary)',
                                                        }}
                                                    >
                                                        <MenuItem value="ALL">Alle</MenuItem>
                                                        <MenuItem value="WITH_ATTACHMENTS">Mit Anhängen</MenuItem>
                                                        <MenuItem value="WITHOUT_ATTACHMENTS">Ohne Anhänge</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <FormControl
                                                    fullWidth
                                                    size={isMobile ? "medium" : "small"}
                                                    sx={{
                                                        '& .MuiInputLabel-root': {
                                                            fontSize: isMobile ? '16px' : '14px',
                                                            fontFamily: 'var(--font-family-primary)',
                                                            color: 'var(--text-secondary)',
                                                        },
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 'var(--border-radius-lg)',
                                                            backgroundColor: 'var(--background-paper)',
                                                            '& fieldset': {
                                                                borderColor: 'var(--border-color)',
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: 'var(--primary-main)',
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {!showOnlyAssigned && <>
                                                        <InputLabel sx={{fontSize: isMobile ? '16px' : '14px', fontFamily: 'var(--font-family-primary)'}}>Zugewiesen an</InputLabel>
                                                        <Select
                                                            value={filters.assignedTo}
                                                            label="Zugewiesen an"
                                                            onChange={(e) => {
                                                                setFilters(prev => ({...prev, assignedTo: e.target.value}))
                                                            }}
                                                            sx={{
                                                                minHeight: isMobile ? '48px' : '40px',
                                                                fontSize: isMobile ? '16px' : '14px',
                                                                fontFamily: 'var(--font-family-primary)',
                                                            }}
                                                        >
                                                            <MenuItem value="ALL">Alle</MenuItem>
                                                            <MenuItem value="ME">Mir zugewiesen</MenuItem>
                                                            <MenuItem value="UNASSIGNED">Nicht zugewiesen</MenuItem>
                                                            {getUniqueAssignedUsers.map((user) => (
                                                                <MenuItem key={user.id} value={user.id}>
                                                                    {user.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select></>
                                                    }

                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Stack>
                            </Collapse>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            {/* Task List */}
            {isMobile ? renderMobileView() : renderDesktopView()}

            {filteredAndSortedTasks.length === 0 && (
                <Box sx={{textAlign: 'center', py: 4}}>
                    <Typography variant='body1' color='text.secondary'>
                        {debouncedSearchTerm || Object.values(filters).some(f => f !== 'ALL')
                            ? 'Keine Aufgaben gefunden, die den Suchkriterien entsprechen'
                            : showArchived
                                ? (showOnlyAssigned ? 'Keine archivierten Aufgaben zugewiesen' : 'Keine archivierten Aufgaben gefunden')
                                : (showOnlyAssigned ? 'Keine Aufgaben zugewiesen' : 'Keine Aufgaben gefunden')
                        }
                    </Typography>
                </Box>
            )}

            {selectedTaskId && (
                <TaskDetail
                    taskId={selectedTaskId}
                    isOpen={selectedTaskId !== null}
                    onClose={() => setSelectedTaskId(null)}
                    onTaskUpdated={() => {
                        fetchTasksWithAttachments();
                    }}
                />
            )}
        </Box>
    );
}
